import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

const AdminMeetLearnDashboard = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    facilitator: '',
    topic: 'TBA',
    description: 'Details coming soon',
    time: 'TBA',
    audience: 'Open to all',
    registration: 'Information to follow'
  });

  // Check authentication
  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/meet-learn');
    } else {
      loadSessions();
    }
  }, [navigate]);

  const loadSessions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/meet-learn`);
      if (!response.ok) throw new Error('Failed to load sessions');
      const data = await response.json();
      setSessions(data);
      setError('');
    } catch (err) {
      setError('Failed to load sessions: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      formDataToSend.append(key, formData[key]);
    });
    if (file) {
      formDataToSend.append('image', file);
    }

    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId
        ? `${API_URL}/api/meet-learn/${editingId}`
        : `${API_URL}/api/meet-learn`;

      const response = await fetch(url, {
        method,
        body: formDataToSend
      });

      if (!response.ok) throw new Error('Failed to save session');

      await loadSessions();
      resetForm();
      setShowForm(false);
    } catch (err) {
      setError('Failed to save session: ' + err.message);
      console.error(err);
    }
  };

  const handleEdit = (session) => {
    setFormData({
      date: session.date,
      facilitator: session.facilitator,
      topic: session.topic,
      description: session.description,
      time: session.time,
      audience: session.audience,
      registration: session.registration,
      imageUrl: session.imageUrl || ''
    });
    setEditingId(session._id);
    setFile(null);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this session?')) {
      try {
        const response = await fetch(`${API_URL}/api/meet-learn/${id}`, {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete session');
        await loadSessions();
      } catch (err) {
        setError('Failed to delete session: ' + err.message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      date: '',
      facilitator: '',
      topic: 'TBA',
      description: 'Details coming soon',
      time: 'TBA',
      audience: 'Open to all',
      registration: 'Information to follow'
    });
    setFile(null);
    setEditingId(null);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    navigate('/admin/meet-learn');
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div>
            <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>
              📅 Meet & Learn Admin
            </h1>
            <p style={{ color: '#64748b', marginTop: '0.5rem' }}>Manage sessions and facilitators</p>
          </div>
          <button
            onClick={handleLogout}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Logout
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '2rem',
            border: '1px solid #fca5a5'
          }}>
            {error}
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '12px',
            marginBottom: '2rem',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            border: '2px solid #2563eb'
          }}>
            <h2 style={{ color: '#1e293b', marginBottom: '1.5rem' }}>
              {editingId ? '✏️ Edit Session' : '➕ Add New Session'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>

                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#1e293b' }}>
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#1e293b' }}>
                    Facilitator Name *
                  </label>
                  <input
                    type="text"
                    name="facilitator"
                    value={formData.facilitator}
                    onChange={handleInputChange}
                    placeholder="e.g., Linda Metcalf"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#1e293b' }}>
                    Topic
                  </label>
                  <input
                    type="text"
                    name="topic"
                    value={formData.topic}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#1e293b' }}>
                    Time
                  </label>
                  <input
                    type="text"
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    placeholder="e.g., 14:00-16:00"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#1e293b' }}>
                    Audience
                  </label>
                  <input
                    type="text"
                    name="audience"
                    value={formData.audience}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#1e293b' }}>
                    Registration
                  </label>
                  <input
                    type="text"
                    name="registration"
                    value={formData.registration}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#1e293b' }}>
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    fontFamily: 'Arial, sans-serif'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#1e293b' }}>
                  Photo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px dashed #2563eb',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    cursor: 'pointer'
                  }}
                />
                <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  Upload JPG, PNG, or other image format
                </p>

                {/* Photo Preview */}
                {(file || (editingId && formData.imageUrl)) && (
                  <div style={{ marginTop: '1rem' }}>
                    <p style={{ color: '#374151', fontWeight: '500', marginBottom: '0.5rem' }}>Preview:</p>
                    <img
                      src={file ? URL.createObjectURL(file) : formData.imageUrl}
                      alt="Preview"
                      style={{
                        maxWidth: '150px',
                        maxHeight: '200px',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  type="submit"
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {editingId ? 'Update Session' : 'Create Session'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowForm(false);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#64748b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Add Session Button */}
        {!showForm && (
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              marginBottom: '2rem'
            }}
          >
            ➕ Add New Session
          </button>
        )}

        {/* Sessions Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#6b7280' }}>Loading sessions...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '12px',
            textAlign: 'center',
            color: '#6b7280'
          }}>
            No sessions yet. Create your first one!
          </div>
        ) : (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#1e293b' }}>Date</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#1e293b' }}>Facilitator</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#1e293b' }}>Topic</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#1e293b' }}>Photo</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#1e293b' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session, index) => (
                    <tr key={session._id} style={{
                      borderBottom: '1px solid #e5e7eb',
                      backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                    }}>
                      <td style={{ padding: '1rem', color: '#1e293b', fontWeight: '500' }}>
                        {session.date ? new Date(session.date).toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' }) : '(Geen datum)'}
                      </td>
                      <td style={{ padding: '1rem', color: '#1e293b' }}>
                        {session.facilitator}
                      </td>
                      <td style={{ padding: '1rem', color: '#64748b' }}>
                        {session.topic === 'TBA' ? '(TBA)' : session.topic}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {session.imageUrl ? (
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            backgroundImage: `url(${session.imageUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }} />
                        ) : (
                          <span style={{ color: '#9ca3af' }}>No photo</span>
                        )}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <button
                          onClick={() => handleEdit(session)}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            marginRight: '0.5rem',
                            fontSize: '0.85rem'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(session._id)}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMeetLearnDashboard;
